import {
    IGameRunnerService,
    IInitialOpenGameParams,
    IPersistedInitialOpenGameParams
} from "./game-runner.service.interface";
import {ServiceBase} from "../ServiceBase";
import {
    IRunningGameController,
    IRunningGameControllerViewModel
} from "./interfaces/game-controller/running-game-controller.interface";
import {observable, runInAction} from "mobx";
import {IRegulatedCompanyModel} from "../companies/regulated-company-model.interface";
import {GameModeEnum} from "./interfaces/game-context/game-context-view-model.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {SessionStorageKeys} from "../storage/session-storage-keys";
import {GNWP_SOURCES} from "../../../sdk/src/components/external_links/external_links_consts";
import {IGameRunnerProxy} from "../../../common/interfaces/game-runner-proxy.interface";
import {Check} from "@sparkware/app-utils";
import {DialogButtonClickHandleResultEnum} from "../dialog/dialog.service.interface";
import {UndefinedString} from "../../../common/types/nullable-types";
import {RunningGameBrowserTabController} from "./running-game-browser-tab-controller";
import {
    HybridEmbeddedGameLoadingIndicatorModel
} from "./loading-indicators/hybrid-embedded-game-loading-indicator.model";
import {WebAppGameLoadingIndicatorModel} from "./loading-indicators/web-app-game-loading-indicator.model";
import {IGameLoadingIndicatorModel} from "./loading-indicators/game-loading-indicator-model.interface";
import {GameStepsExecutionResultEnum} from "./interfaces/game-step/game-run-step.interface";
import {OpenGameParameters} from "../games/game-info";

export class GameRunnerService extends ServiceBase implements IGameRunnerService, IGameRunnerProxy {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.gameTabController = new RunningGameBrowserTabController(this, this.services);

        this._reOpenPendingGame();

        this._subscribeToSdkEvents();

        this.reaction(() => this.services.user.isAuthorized,
            async () => {
                await this.currentRunningGame?.closeGame({
                    shouldSuggestNextGame: false
                });
            });
    }

    readonly gameTabController: RunningGameBrowserTabController;



    get gameFrameZIndex(): number {
        return this.services.sdk.overlayZIndex.GAME_FRAME;
    }

    private async _reOpenPendingGame(): Promise<void> {
        const persistedGameParams = this.services.sessionStorage.getJson<IPersistedInitialOpenGameParams>(SessionStorageKeys.currentGameParams);
        if (!persistedGameParams) {
            return;
        }

        try {
            const {gameType, ...initialGameParams} = persistedGameParams;

            const gameInfo = await this.services.games.getGameInfo(gameType);

            if (!gameInfo) {
                return;
            }

            await this.openGame({
                ...initialGameParams,
                gameInfo: gameInfo
            });
        } finally {
            this.services.sessionStorage.removeItem(SessionStorageKeys.currentGameParams);
        }

    }

    private _subscribeToSdkEvents(): void {
        this.services.sdk.events.onDepositWidgetClosed(() => {
            this._refreshCurrentGameBalance();
        });

        this.services.sdk.events.onExternalLinkClosed((linkSource) => {
            if (GNWP_SOURCES.CASHIER === linkSource) {
                this._refreshCurrentGameBalance();
            }
        });
    }

    private _refreshCurrentGameBalance(): void {
        if (this.currentRunningGame && this.currentRunningGame.gameCommunicationChannel.supportsBalanceRefresh) {
            this.currentRunningGame.gameCommunicationChannel.refreshBalance();
        }
    }


    async openGame(initialRunGameParams: IInitialOpenGameParams): Promise<IRunningGameControllerViewModel | null> {


        try {
            switch (initialRunGameParams.gameMode) {
                case GameModeEnum.Real:
                    return await this._openRealGame(initialRunGameParams);
                case GameModeEnum.Demo:
                    return await this._openDemoGame(initialRunGameParams);
                case GameModeEnum.History:
                    return await this._openHistoryGame(initialRunGameParams);
                default:
                    throw new Error(`Invalid gameMode ${initialRunGameParams.gameInfo}`);
            }
        } catch (err) {
            this.services.logger.error(`Failed to open game ${initialRunGameParams.gameInfo.gameType} in mode ${initialRunGameParams.gameMode}`, err);
            this._logOpenGameError(err, initialRunGameParams);
            this._showGameOpenError(err);
        }
    }

    private _showGameOpenError(err: any): void {
        let messageText = null;
        if (err?.error_code) {
            messageText = this.services.regulation.getErrorMessage(err.error_code);
        }

        if (!messageText) {
            messageText = this.services.regulation.getGeneralErrorMessage();
        }

        this.services.dialog.showSimpleTextModal({
            text: messageText,
            showEmptyTitle: true,
            buttons: [
                {
                    text: this.services.translation.translate('OK'),
                    buttonPreset: 'secondary',
                    onClick: async () => DialogButtonClickHandleResultEnum.CloseDialogAndContinue
                }
            ]
        });

    }

    private _logOpenGameError(err: any, initialRunGameParams: IInitialOpenGameParams): void {
        try {
            let errMsgToLog: string;
            if (Check.isObject(err)) {
                errMsgToLog = JSON.stringify(err);
            } else {
                errMsgToLog = err?.toString();
            }
            this.services.analytics.sendAnalytics("game_load_error", errMsgToLog, initialRunGameParams.gameInfo.gameType.toString());
        } catch (logError) {
            this.services.logger.error('Failed to log game error', logError);
        }

    }

    private async _openRealGame(initialRunGameParams: IInitialOpenGameParams): Promise<IRunningGameControllerViewModel | null> {
        return await this._runGame(initialRunGameParams, company => company.openRealGame(initialRunGameParams));
    }

    private async _openDemoGame(initialRunGameParams: IInitialOpenGameParams): Promise<IRunningGameControllerViewModel | null> {
        return await this._runGame(initialRunGameParams, company => company.openDemoGame(initialRunGameParams));
    }

    private async _openHistoryGame(initialRunGameParams: IInitialOpenGameParams): Promise<IRunningGameControllerViewModel | null> {
        if (!initialRunGameParams.historyGameParams) {
            throw new Error('Missing history game params');
        }
        return await this._runGame(initialRunGameParams, company => company.openHistoryGame(initialRunGameParams));
    }

    @observable.ref
    gameLoadingIndicator: IGameLoadingIndicatorModel | null = null;

    private _setGameLoadingIndicator(loadingIndicator: IGameLoadingIndicatorModel | null): void {
        runInAction(() => {
            this.gameLoadingIndicator?.dispose();
            this.gameLoadingIndicator = loadingIndicator;
        })
    }

    private _createGameLoadingIndicator(): IGameLoadingIndicatorModel {
        if (this.services.device.hybrid.isEmbedded) {
            return new HybridEmbeddedGameLoadingIndicatorModel(this.services, this);
        } else {
            return new WebAppGameLoadingIndicatorModel(this.services);
        }
    }

    private async _closeCurrentRunningGame(): Promise<GameStepsExecutionResultEnum> {
        if(this.currentRunningGame) {
            return await this.currentRunningGame.closeGame({
                shouldSuggestNextGame: false
            });
        }

        return GameStepsExecutionResultEnum.Continue;
    }

    private _runGameStarted = false;
    private async _runGame(initialRunGameParams: IInitialOpenGameParams, openGame: (company: IRegulatedCompanyModel) => Promise<IRunningGameController | null>): Promise<IRunningGameControllerViewModel | null> {
        if (this._runGameStarted) { //to prevent the case when the user double-click on the game
            return;
        }

        try {
            this._runGameStarted = true;
            const closeCurrentGameResult = await this._closeCurrentRunningGame()

            if (closeCurrentGameResult !== GameStepsExecutionResultEnum.Continue) {
                return;
            }

            this._setGameLoadingIndicator(this._createGameLoadingIndicator());


            const company = await this.services.companies.getRegulatedCompany(initialRunGameParams.gameInfo.companyName);
            const gameController = await openGame(company);

            if (!gameController) {
                return null;
            }
            this._setCurrentRunningGame(gameController);
            return gameController;
        } finally {
            this._runGameStarted = false;
            this._setGameLoadingIndicator(null);
        }
    }

    @observable.ref
    currentRunningGame: IRunningGameController | null = null;

    private _setCurrentRunningGame(gameController: IRunningGameController): void {
        gameController.createWaitForClosePromise();

        runInAction(() => {
            this.currentRunningGame = gameController;
        });

        gameController.triggerLegacyGameOpenEvent();
        this.services.overlay.disableScrolling();
        gameController.waitForGameToClose().then(() => {
            runInAction(() => {
                this.currentRunningGame = null
            });
            gameController.triggerLegacyGameCloseEvent();
            this.services.overlay.enableScrolling();
        });
    }

    closeGameLoadingIndicator(): void {
        this._setGameLoadingIndicator(null);
    }


    /**
     * Legacy adapter implementations
     */

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    async closeRunningGame(): Promise<void> {
        if (this.currentRunningGame) {
            await this.currentRunningGame.closeGame({
                shouldSuggestNextGame: false
            });
        }
    }

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    get isGameRunningLegacy(): boolean {
        return Boolean(this.currentRunningGame) && this.currentRunningGame.gameContext.gameMode !== GameModeEnum.History;
    }

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    get isGameRunningInFreePlayModeLegacy(): boolean {
        return Boolean(this.currentRunningGame?.gameContext.freePlayContext.isFreePlay);
    }

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    get currentRunningGameCompanyNameLegacy(): UndefinedString {
        return this.currentRunningGame?.gameContext.company.companyName;
    }

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    stopCurrentGameAutoplayLegacy(): void {
        this.currentRunningGame?.gameCommunicationChannel.stopAutoplay();
    }
    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    continuePlayCurrentGameLegacy(): void {
        this.currentRunningGame?.gameCommunicationChannel.resumeGamePlay();
    }

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    shouldShowRealityCheckMessageWhileGameIsOpenLegacy(): boolean {
        if(this.currentRunningGame) {
            return (this.currentRunningGame.gameContext.gameInternalRealityCheckInterval.getRealityCheckExpectedByTheGame().expectedValueByTheGame === 0
                    || this.currentRunningGame.company.shouldShowRealityCheckMessageWhileGameIsOpen());
        }
        return false;
    }

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    shouldShowTimeLimitMessageWhileGameIsOpenLegacy(): boolean {
        return Boolean(this.currentRunningGame?.company.shouldShowTimeLimitMessageWhileGameIsOpen());
    }

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    get isOpenGameInProgress():boolean
    {
        return Boolean(this.gameLoadingIndicator);
    }

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    async openRealGameFromSdkRequest(gameType: number, options?: OpenGameParameters): Promise<void> {
        const gameInfo = await this.services.games.getGameInfo(gameType);
        if(!gameInfo) {
            throw new Error(`There is no game in layout with gameType ${gameType}`);
        }

        await gameInfo.open({
            isRealMode: true,
            ...options
        });
    }

    /**
     * This should not be used in Orbit code it is only for old SDK use
     * @deprecated
     */
    async openDemoGameFromSdkRequest(gameType: number, options?: OpenGameParameters): Promise<void> {
        const gameInfo = await this.services.games.getGameInfo(gameType);
        if(!gameInfo) {
            throw new Error(`There is no game in layout with gameType ${gameType}`);
        }

        await gameInfo.open({
            isRealMode: false,
            ...options
        });
    }
}
